import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from '../Sidebar';
import { ArrowRight, LifeBuoy, LayoutDashboard, PlusCircle, ListChecks, Monitor, BookOpen, Gauge, QrCode } from 'lucide-react';
import useTelaMobile from '../../utils/useTelaMobile';
import HelpCenter from '../HelpCenter';
import ModuloHeader from '../ModuloHeader';
import ChamadosAbertura from './atendimento/ChamadosAbertura';
import ChamadosConsulta from './atendimento/ChamadosConsulta';
import ChamadosAtivos from './ativos/ChamadosAtivos';
import ChamadosCalendario from './atendimento/ChamadosCalendario';
import ChamadosManual from './gps-ti/ChamadosManual';
import ChamadosPortaria from './atendimento/ChamadosPortaria';
// ⚠️ As abas E A FILA DE MANUTENÇÃO deste hub saem do SERVIDOR (subsistema de
//    acessos): quem decide é a matriz do Painel Admin, não uma lista aqui dentro.
//    O antigo espelho `chamados-fila-manutencao.js` foi aposentado em 2026-09-22 —
//    a fila virou a capacidade `chamados.fila.manutencao`, lida de `caps`.
import { useCapacidades } from '../../utils/capacidades';
import ChamadosFaixaFila, { rotuloDaFila } from './ChamadosFaixaFila';
import ChamadosIndicadores from './atendimento/ChamadosIndicadores';
import ChamadosPatrimonio from './etiquetas/ChamadosPatrimonio';
import { perfilNoModulo } from '../../utils/perfisDerivados';
// ⚠️ TEMPORÁRIO: a fase de teste do módulo (ver `chamados-piloto.js` no server).
import { LOJAS_PILOTO_CHAMADOS, lojaNoPilotoChamados } from '../../utils/escopoUsuario';

// =====================================================================
// Hub do Módulo Chamados — abertura de chamado para TI (fase 1).
//
// Substitui o Auvo como fonte única. O fluxo tem TRÊS atos, cada um com dono:
//   1. Gerente / Gerente Plantao ABRE (própria loja)
//   2. Gerente TI TRIA: prioridade + técnico
//   3. TI EXECUTA e ENCERRA, com observação + fotos do serviço
//   4. A LOJA VALIDA (gerente, gerente de plantão ou coordenador de qualidade),
//      e é a validação que FECHA o chamado
//
// ⚠️⚠️ O ATO 4 VOLTOU EM 31/08/2026, e voltou DIFERENTE das duas vezes anteriores.
//    Ele existiu como etapa até 21/08 (a loja encerrava validando) e foi
//    removido; em 27/08 virou um campo obrigatório DENTRO do encerramento — o
//    técnico nomeava um gerente numa lista, no aparelho dele, o que era
//    declaração e não prova. Agora é o gerente clicando no próprio login, DEPOIS
//    do encerramento, no gesto da Certificação de equipamento.
//    ⚠️ Quem ENCERRA segue sendo o técnico: validar não encerra, e a loja não
//    reabre chamado. O inventário do que segue removido está no `chamados-fluxo.js`.
//
// ⚠️ E o atendimento ganhou uma parada nova, entre o ato 3 e o encerramento: o
//    `Finalizado`, quando o técnico terminou o que estava na mão dele e o chamado
//    espera um TERCEIRO — o Invgate (Sonda) ou a chegada de uma peça.
//
// ⚠⚠ A PALETA MUDA COM A FILA desde 17/09/2026: roxo `#6b21a8` no T.I — pedido
// da Julia em 27/08, "a cor do aplicativo Auvo" — e índigo `#4338ca` na fila de
// Manutenção, a cor do time dono dela. As telas NÃO recebem cor por prop: elas
// leem as variáveis `--ch-*` do `index.css`, e o modificador de classe deste hub
// (`modulo-chamados--manutencao`) troca todas de uma vez. O porquê e o risco
// assumido estão em `docs/ARQUITETURA.md` › Paleta dos módulos.
// Paleta ROXO #6b21a8 (2026-08-27) — pedido da Julia: "a cor do aplicativo Auvo",
// que é o sistema que este módulo substitui. A cor vira, aqui, continuidade para
// quem usava o Auvo: quem abre chamado reconhece o lugar antes de ler o título.
//
// ⚠️ NASCEU ROSA-ESCURO #be185d (21/08/2026), escolhido por eliminação. O roxo
//    não tinha sido cogitado por vizinhança com o VIOLETA do Projetos (#7c3aed) —
//    e essa vizinhança continua existindo. O que a torna aceitável é o alcance do
//    Projetos: ele é do perfil `Presidencia`, uma pessoa (o Sr. Marco, ID 44), que
//    não alcança NENHUM outro módulo do portal. Os dois nunca aparecem na mesma
//    jornada, que é o teste da seção de paleta. Se um dia o Projetos abrir para
//    mais gente, é o Chamados que se afasta — o roxo aqui tem um porquê externo
//    (o Auvo), o violeta de lá é arbitrário.
// ⚠️ E é PURPLE-800, não o purple-700: além de ser o tom mais próximo do Auvo, é
//    o que afasta do violet-600 do Projetos — trocar por um roxo mais claro
//    reaproxima os dois.
//
// Registrada em docs/ARQUITETURA.md › Paleta dos módulos. Padrão dos outros hubs:
// texto BRANCO sobre o gradiente cor→tom mais escuro (#6b21a8→#581c87); acentos
// sobre fundo branco usam a própria #6b21a8.
//
// ⚠️ A CONSULTA E A FILA SÃO A MESMA ABA, não duas. A lista é a mesma e o que
// muda são as ações disponíveis, que já dependem do papel de quem olha — duas
// telas mostrando a mesma tabela com filtro diferente seriam duas coisas para
// manter e uma dúvida a mais ("em qual delas eu vejo o meu?").
//
// ⚠️ São QUATRO abas no total desde 27/08 (o *Manual do TI* entrou). A loja
//    enxerga TRÊS desde 28/08 (abrir, chamados e manual) — a de *Equipamentos*
//    é do TI, que é quem mantém o parque. A cortina que escondia a fila dela
//    durou um dia; ver `chamados.aba.consulta` no catálogo de capacidades.
// =====================================================================

const COR = 'var(--ch-cor)';
const COR_ESCURA = 'var(--ch-cor-escura)';
const COR_CLARA = 'var(--ch-cor-clara)';

// ---------------------------------------------------------------------
// ACESSO TOTAL DA DIRETORIA (Julia, 2026-08-31)
// ---------------------------------------------------------------------
// A `Diretoria` não alcançava nada deste módulo até aqui. Passou a alcançar
// TUDO — as quatro abas, o calendário e todas as ações da fila.
//
// ⚠️ Ela é escrita nas listas abaixo como qualquer outro perfil; o que NÃO se faz
//    é somá-la ao bypass do Admin no `alcanca()`. O bypass do Admin existe porque
//    ele é universal no portal inteiro; a Diretoria entra por decisão DESTE
//    módulo, e é aqui que se lê quem entrou.
// ⚠️ `Diretoria Op` NÃO aparece nestas listas e não deve aparecer: o
//    `perfilNoModulo(perfil, 'chamados')` a traduz para `Diretoria` na leitura.
//    Escrever o nome dela aqui criaria a segunda lista que o `perfilNoModulo`
//    existe para evitar.
// ⚠️⚠️ O `PERMISSIONS` DESTE ARQUIVO DEIXOU DE EXISTIR EM 18/09/2026, e não foi
//    perda: ele virou o CATÁLOGO DE CAPACIDADES do servidor
//    (`server/acessos/capacidades.js` › `chamados.aba.*`), onde os mesmos perfis
//    estão declarados como PADRÃO e podem ser alterados pela matriz do Painel
//    Admin — sem deploy, que era a dor.
//
//    Quem procurar aqui a resposta de "quem vê a aba X" encontra este comentário:
//    a resposta mora no catálogo, e o que VALE é padrão + as exceções gravadas no
//    banco. Cada decisão registrada nas listas antigas (o `Gerente Adm` fora da
//    abertura, a loja dentro do SLA, o Patrimônio só do Admin) foi copiada para
//    lá com o comentário dela — nada se perdeu no caminho.
//
// ⚠️ A trava de verdade continua sendo do SERVIDOR: cada rota tem a sua, e esta
//    tela só decide o que aparece. Esconder card aqui nunca protegeu nada.

// ---------------------------------------------------------------------
// QUEM NÃO PASSA PELA PORTARIA (2026-08-28)
// ---------------------------------------------------------------------
// A portaria (`ChamadosPortaria.js`) pergunta o departamento e leva a loja ao
// MANUAL. Para quem ATENDE chamado, isso é pedágio diário: o `Gerente TI` e o
// `TI` abrem o módulo para trabalhar a fila, não para ler o guia. Eles entram
// direto na consulta.
//
// ⚠️⚠️ O `Admin` NÃO ESTÁ AQUI, e é decisão da Julia (28/08) — tomada contra a
//    proposta oposta. Parece esquecimento a quem lê por cima; não é. A regra de
//    entrada deste módulo deixa de ser "quem tem mais poder tem menos fricção":
//    o Admin tem bypass em toda rota do portal e mesmo assim passa pela
//    portaria, porque a equipe de dados é composta de Admins e não é ela quem
//    atende chamado. Quem atende é que ganha o caminho curto.
//    ⚠️ NÃO "consertar" por analogia com o bypass do Admin das capacidades:
//    ali o assunto é PERMISSÃO, aqui é DESTINO DE ENTRADA. São coisas
//    diferentes, e o Admin segue alcançando tudo depois de escolher.
//
// ⚠️⚠️ A `Diretoria` TAMBÉM PASSA pela portaria, pela MESMA razão do Admin —
//    mesmo tendo acesso total ao módulo desde 31/08. Acesso total é PERMISSÃO;
//    esta lista é DESTINO DE ENTRADA, e quem ganha o caminho curto é quem ATENDE
//    chamado. Um diretor entra no módulo para acompanhar, não para trabalhar a
//    fila. Parece esquecimento a quem lê por cima; não é.
//
// ⚠️ Lista única e derivada por função — nunca um `if (userRole === 'TI' || …)`
//    espalhado pela tela.
const PERFIS_SEM_PORTARIA = ['Gerente TI', 'TI'];

/** Esta pessoa entra direto na fila, sem a pergunta de departamento? */
const pulaPortaria = (perfil) => PERFIS_SEM_PORTARIA.includes(perfil);

// =====================================================================
// AS ABAS DE CADA FILA (Julia, 2026-09-17)
// =====================================================================
// ⚠️⚠️ ABA QUE NÃO SE APLICA À FILA **NÃO APARECE**, em vez de aparecer e
//    decepcionar: o *Manual do TI* é o GPS-TI (contato e escala dos técnicos de
//    TI) e *Equipamentos* é o parque de TI — numa fila de Manutenção os dois
//    respondem sobre o time errado.
//
// ⚠️ `patrimonio` FICA FORA DESTE RECORTE de propósito, e não é esquecimento: o
//    pool de etiquetas tem os DOIS departamentos (`TI-00001` e `M-0001`) numa
//    tela só, e é de implantação (só `Admin`). Recortá-lo por fila obrigaria a
//    trocar de fila para emitir o lote do outro departamento — atrito sem ganho.
//
// ⚠️ Quem não está listado aqui vale para TODAS as filas. A lista diz o que é
//    EXCLUSIVO de uma fila, e é curta de propósito: quase tudo neste módulo é
//    genérico e funciona nas duas (a esteira, o SLA, a abertura).
const ABAS_EXCLUSIVAS_DA_FILA = {
    manual: 'TI',   // o GPS-TI é o guia da equipe de TI
    // ⚠️ `ativos` SAIU desta lista em 17/09/2026, junto com a mudança de tabela: o
    //    equipamento das duas filas passou a viver na `dtbChamadoAtivo` (coluna
    //    `DEPARTAMENTO`), então *Equipamentos* mostra o parque DA FILA ATUAL e
    //    faz sentido nas duas. Enquanto ele apontava para o cadastro da
    //    Certificação, a aba seria a porta de um cadastro de outro módulo.
};

/** Esta aba aparece nesta fila? */
const abaValeNaFila = (id, fila) => !ABAS_EXCLUSIVAS_DA_FILA[id] || ABAS_EXCLUSIVAS_DA_FILA[id] === fila;

const modulesList = [
  {
    id: 'abrir',
    label: 'Abrir chamado',
    icon: PlusCircle,
    color: COR,
    bgColor: COR_CLARA,
    desc: 'Registre um problema de equipamento ou sistema da sua loja — com foto, se ajudar a explicar',
    stat: 'Loja',
  },
  {
    id: 'consulta',
    label: 'Chamados',
    icon: ListChecks,
    color: COR,
    bgColor: COR_CLARA,
    desc: 'A fila de trabalho: o que está aberto, de quem se espera e há quanto tempo está parado',
    stat: 'Acompanhamento',
  },
  {
    id: 'manual',
    label: 'Manual do TI',
    icon: BookOpen,
    color: COR,
    bgColor: COR_CLARA,
    desc: 'O GPS-TI com o contato e a escala dos técnicos — a versão da loja não traz esses dados',
    stat: 'Uso interno',
  },
  {
    id: 'indicadores',
    label: 'Indicadores',
    icon: Gauge,
    color: COR,
    bgColor: COR_CLARA,
    desc: 'O SLA: quanto do que a loja abre é atendido dentro do prazo — meta de 90%',
    stat: 'Acompanhamento',
  },
  {
    id: 'patrimonio',
    label: 'Patrimônio',
    icon: QrCode,
    color: COR,
    bgColor: COR_CLARA,
    desc: 'As etiquetas com QR Code coladas no equipamento — emissão, vínculo e o pacote da gráfica',
    stat: 'Implantação',
  },
  {
    id: 'ativos',
    label: 'Equipamentos',
    icon: Monitor,
    color: COR,
    bgColor: COR_CLARA,
    desc: 'O parque de TI de cada loja — é dele que sai o seletor de equipamento na abertura',
    stat: 'Cadastro',
  },
];

const ChamadosPainel = () => {
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : {};
  // ⚠️ Traduz o perfil UMA vez, na leitura, e SEMPRE com o módulo: sem o módulo,
  // um perfil derivado viraria o perfil-base em toda parte do portal.
  const userRole = perfilNoModulo(user?.perfil, 'chamados') || '';

  // ⚠️ ERA `useState(window.innerWidth <= 768)` SEM SETTER — ou seja, congelado no
  //    valor do momento em que a tela montou: girar o aparelho não o atualizava, e
  //    o cabeçalho seguia no layout errado. O hook mede de verdade.
  //    (O padrão copiado aparece em ~151 pontos do portal; só este foi corrigido.)
  // ⚠️ `ehTabletOuMenor` (≤1024px) existe porque o tablet em pé usa o layout
  //    de computador e, mesmo assim, não tem largura para tudo — a faixa de
  //    fila usa isso para esconder o escopo e caber numa linha só.
  // As abas que esta pessoa alcança — uma chamada por sessão de página,
  // compartilhada entre os componentes (ver `src/utils/capacidades.js`).
  const { caps, carregando: carregandoCaps, erro: erroCaps } = useCapacidades();
  // A fila de Manutenção é a capacidade `chamados.fila.manutencao`, resolvida pelo
  // MESMO backend que nega a rota (o espelho de IDs foi aposentado em 2026-09-22).
  // ⚠️ Piso ['TI']: quem chega a este módulo já passou pelas listas de perfil dele;
  //    a de Manutenção só se soma quando a capacidade a concede. Tela em branco
  //    (lista vazia) seria o sintoma mudo que o piso evita.
  const podeVerFilaManutencao = caps.has('chamados.fila.manutencao');
  const filasVisiveis = ['TI', ...(podeVerFilaManutencao ? ['Manutencao'] : [])];
  const { ehMobile: isMobile, ehTabletOuMenor } = useTelaMobile();
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
  // ---------------------------------------------------------------------
  // A ABA INICIAL — e o `?tab=abrir` que MORREU em 2026-08-28.
  //
  // ⚠️⚠️ ELE EXISTIU ENTRE 27 E 28/08 e foi removido junto com a PORTARIA. O
  //    GPS-TI fechava cada ficha com um link para `/chamados?tab=abrir`, para
  //    quem estava de pé na loja não ter de procurar a aba certa. Com a portaria
  //    na frente, esse atalho não teria mais efeito — e um `?tab=` que a URL
  //    aceita mas não obedece é pior que nenhum: alguém favorita o link, ele
  //    para de levar ao formulário, e o sintoma não aponta para a causa.
  //
  // ⚠️ O CAMINHO CURTO NÃO SUMIU, MUDOU DE MECANISMO: o botão do guia agora
  //    conversa com esta tela por `postMessage` (ver `ChamadosManual.js`), o que
  //    troca de aba SEM recarregar a página e ainda carrega a ficha que a pessoa
  //    estava lendo — coisa que a URL nunca fez.
  //
  // ⚠️ Quem removeu isto precisa saber que sobrou o `href` do guia apontando
  //    para `/chamados` (sem `?tab=`), de propósito: é o fallback de quando o JS
  //    da ponte não roda, e cai na portaria como qualquer outra entrada.
  // ---------------------------------------------------------------------
  // ⚠️ Quem pula a portaria cai na FILA, não na Visão Geral: é o trabalho dele.
  //    ⚠️ NÃO validar a permissão aqui — o `useEffect` logo abaixo já devolve
  //    para a Visão Geral quem não alcançar a aba (falha fechada em UM lugar só).
  // ---------------------------------------------------------------------
  // A ENTRADA PELO QR CODE DA ETIQUETA (2026-09-11)
  // ---------------------------------------------------------------------
  // Quem escaneou o adesivo do equipamento chegou aqui pela rota `/q/:codigo`
  // (ver `ChamadosEtiquetaEntrada.js`), que já identificou o aparelho e o passou
  // no `state` da navegação.
  //
  // ⚠️ VEM NO `state` DA ROTA, não na URL do `/chamados`: o endereço do adesivo é
  //    o `/q`, e é ele que tem de ser estável. Pendurar o equipamento na URL do
  //    módulo criaria um SEGUNDO endereço que as etiquetas passariam a depender —
  //    exatamente o acoplamento que o `/q` existe para evitar.
  // ⚠️ Lido UMA vez, na montagem: o `state` sobrevive ao recarregar a página no
  //    React Router, e sem o congelamento a pessoa que trocasse de aba e voltasse
  //    reencontraria o equipamento da etiqueta escolhido de novo, sem ter pedido.
  const location = useLocation();
  const [etiqueta] = useState(() => location.state?.etiqueta || null);

  const [activeTab, setActiveTab] = useState(
    // ⚠️ Quem chega pelo adesivo cai direto na ABERTURA — nem portaria, nem visão
    //    geral. Ele já está de pé na loja com o aparelho quebrado na mão: qualquer
    //    tela no meio do caminho é pedágio.
    () => (etiqueta ? 'abrir' : pulaPortaria(userRole) ? 'consulta' : 'dashboard'),
  );
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  // ---------------------------------------------------------------------
  // A PORTARIA e o filtro que vem do guia.
  // ---------------------------------------------------------------------
  // ⚠️ Estado em MEMÓRIA e nada de `localStorage`: a pergunta volta a cada
  //    visita ao módulo, que foi o pedido (Julia, 28/08). Trocar de aba DENTRO
  //    do módulo não remonta este componente, então a portaria não reaparece a
  //    cada clique — só ao entrar no módulo de novo.
  const [passouPortaria, setPassouPortaria] = useState(() => pulaPortaria(userRole) || !!etiqueta);

  // ---------------------------------------------------------------------
  // PARA QUAL FILA O FORMULÁRIO ABRE (17/09/2026)
  // ---------------------------------------------------------------------
  // ⚠️⚠️ O VALOR É O DA COLUNA `EQUIPE` ('TI' | 'Manutencao'), e atravessa daqui
  //    até o `INSERT` sem ninguém traduzir no meio — traduzir no caminho é onde
  //    nasce chamado na fila errada, com o equipamento certo.
  // ⚠️ Duas origens, nesta ordem: o ADESIVO (quem escaneou já disse a área — e a
  //    etiqueta chega com a `equipe` JÁ TRADUZIDA pelo servidor, nunca com um
  //    `dep === 'MAN' ? …` aqui) e a PORTARIA. Quem pula a portaria (o TI, o
  //    Gerente TI) cai em 'TI', que é a fila deles.
  // ⚠️ `useState` e não `useMemo`: a portaria ESCREVE nisto. Derivar da etiqueta
  //    a cada render descartaria a escolha feita no modal.
  // ⚠️⚠️ A ETIQUETA NÃO FURA O PORTÃO: adesivo `MAN` escaneado por quem não toca
  //    a implantação nem chega aqui (o `resolver` o para antes), mas se chegasse,
  //    cair em 'TI' é a falha fechada certa — abrir na fila que a pessoa alcança.
  const [equipeAlvo, setEquipeAlvo] = useState(
    () => (etiqueta?.equipe === 'Manutencao' && podeVerFilaManutencao ? 'Manutencao' : 'TI'),
  );

  // A família de equipamento que a ficha do guia indicou, ou `null`.
  // ⚠️ `null` significa "mostre TODOS" — nunca lista vazia. Ver a falha aberta
  //    em `gps-ti-fichas-familias.js` (backend) e `chamados-busca-ativos.js`.
  const [familiaFiltro, setFamiliaFiltro] = useState(null);

  /**
   * Vai para a aba de abrir chamado, opcionalmente já filtrada por família.
   *
   * ⚠️ É a ÚNICA porta de entrada da aba *Abrir chamado* que carrega filtro.
   *    Qualquer outro caminho (clicar na aba, card da Visão Geral) passa por
   *    `irParaAba`, que LIMPA o filtro — senão ele gruda: a pessoa volta pela
   *    aba esperando a lista inteira e recebe o recorte de uma ficha que já
   *    fechou, sem nada na tela explicando por quê.
   */
  const irParaAbertura = (familia = null) => {
    setFamiliaFiltro(familia || null);
    setActiveTab('abrir');
  };

  /**
   * Troca a fila inteira do módulo (a faixa e a portaria chamam aqui).
   *
   * ⚠️⚠️ VOLTA PARA A VISÃO GERAL, e é deliberado: a aba em que a pessoa estava
   *    pode não existir na outra fila (*Manual do TI* e *Equipamentos* são só de
   *    TI). Sem isto, trocar de fila estando no Manual a deixaria numa aba que o
   *    `useEffect` de falha fechada devolveria à Visão Geral no quadro seguinte —
   *    um piscar sem explicação.
   * ⚠️ Limpa o filtro de família pelo mesmo motivo do `irParaAba`: ele é o
   *    recorte de uma ficha da fila anterior.
   */
  const trocarDeFila = (fila) => {
    setEquipeAlvo(fila);
    setFamiliaFiltro(null);
    setActiveTab('dashboard');
  };

  /** Troca de aba pelos caminhos normais — sempre limpando o filtro da ficha. */
  const irParaAba = (id) => {
    setFamiliaFiltro(null);
    setActiveTab(id);
  };

  /**
   * Esta pessoa alcança esta parte do módulo? Serve para ABA e para pedaço de
   * tela (ver a chave 'agenda', que é o calendário dentro da Visão Geral).
   *
   * ⚠️ A resposta vem PRONTA do servidor (`/api/acessos/minhas-capacidades`),
   *    do mesmo resolver que nega na rota — inclusive o bypass do Admin e a
   *    herança dos perfis derivados, que por isso sumiram daqui. Refazer
   *    qualquer parte dessa conta no navegador é como a tela volta a discordar
   *    da rota.
   */
  const alcanca = (id) => caps.has(`chamados.aba.${id}`);

  // ⚠️ DOIS recortes, e a ordem não importa porque eles respondem perguntas
  //    diferentes: `alcanca` é PERMISSÃO (quem é essa pessoa), `abaValeNaFila` é
  //    ESCOPO (esta aba fala da fila em que estou). Juntá-los numa lista só faria
  //    a permissão e o assunto virarem a mesma coisa.
  const allowedModules = modulesList
    .filter((mod) => alcanca(mod.id))
    .filter((mod) => abaValeNaFila(mod.id, equipeAlvo));

  // ⚠️ Derivados, NUNCA escritos à mão. Com a Consulta e o calendário escondidos
  //    da loja (2026-08-27), três pontos desta tela precisam saber quem enxerga o
  //    quê — as abas, o destino pós-abertura e a Visão Geral. Uma segunda lista de
  //    perfis divergiria das capacidades no primeiro ajuste, e o sintoma seria
  //    mudo: o gerente cairia numa aba inexistente e voltaria à Visão Geral sem
  //    ler a confirmação do chamado que acabou de abrir.
  const podeVerConsulta = alcanca('consulta');
  const podeVerAgenda = alcanca('agenda');

  const OverviewWidgets = () => (
    <div>
      <div style={{
        background: `linear-gradient(135deg, ${COR} 0%, ${COR_ESCURA} 100%)`,
        borderRadius: '16px', padding: '40px', color: '#fff',
        marginBottom: '32px', position: 'relative', overflow: 'hidden',
        boxShadow: `0 10px 15px -3px var(--ch-sombra)`,
      }}>
        <div style={{ position: 'relative', zIndex: 10 }}>
          {/* ⚠️ O CARTÃO DA VISÃO GERAL FALA DA FILA EM QUE A PESSOA ESTÁ
              (17/09/2026). Ele dizia "chamados de TI" fixo — e, na fila de
              Manutenção, anunciava o time errado logo no maior texto da tela,
              acima de uma fila de fritadeiras. O escopo entre parênteses é o
              mesmo da faixa: é ele que evita o chamado de fritadeira no TI. */}
          <h2 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '12px', marginTop: 0, color: '#fff' }}>
            Chamados · {rotuloDaFila(equipeAlvo)}
          </h2>
          <p style={{ color: 'var(--ch-cor-clara)', fontSize: '18px', maxWidth: '650px', margin: 0, lineHeight: '1.6' }}>
            {equipeAlvo === 'Manutencao'
              ? <>Abertura e acompanhamento de chamados de <strong>Manutenção</strong> do Grupo
                Correia MC — fritadeira, chapa, ar-condicionado e prédio, da loja até o
                encerramento, com dono em cada etapa.</>
              : <>Abertura e acompanhamento de chamados de <strong>T.I</strong> do Grupo Correia
                MC — POS, KVS, impressora, rede e pinpad, da loja até o encerramento, com dono
                em cada etapa.</>}
          </p>
        </div>
        <div style={{ position: 'absolute', right: '-30px', top: '-40px', opacity: 0.12 }}>
          <LifeBuoy size={250} color="#ffffff" />
        </div>
      </div>

      {/* OS CARDS DE NAVEGAÇÃO VÊM ANTES DA TRILHA (Julia, 2026-08-27).
          Nasceram depois dela, e a ordem antiga tinha uma lógica — explicar o
          fluxo antes de oferecer as portas. Na prática ela cobra um preço a cada
          visita: quem entra no módulo quer IR a algum lugar (abrir, consultar,
          cadastrar), e a trilha é leitura de uma vez só. Ensinada em primeiro,
          ela empurra as portas para baixo da dobra — no celular, sempre.
          A trilha não sumiu: segue logo abaixo, para quem rola. */}
      {/* ⚠️ O respiro de baixo mora AQUI agora, não na trilha. Ele acompanha o
          bloco de cima, não um bloco fixo — trocar a ordem sem mover a margem
          gruda os cards na trilha e dá o dobro do espaço antes da agenda. */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: isMobile ? '12px' : '24px', marginBottom: isMobile ? '16px' : '32px',
      }}>
        {allowedModules.map((mod) => (
          <div
            key={mod.id}
            onClick={() => irParaAba(mod.id)}
            style={{
              backgroundColor: '#fff', borderRadius: '16px', padding: isMobile ? '16px' : '24px',
              border: `1px solid ${COR_CLARA}`, display: 'flex', flexDirection: 'column',
              height: '100%', cursor: 'pointer', position: 'relative', overflow: 'hidden',
              transition: 'all 0.2s', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px -2px rgba(0,0,0,0.1)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.05)'; }}
          >
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px', backgroundColor: mod.color }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <p style={{ fontSize: '12px', fontWeight: '800', textTransform: 'uppercase', color: mod.color, marginBottom: '8px', margin: 0 }}>{mod.stat}</p>
                <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#0f172a', margin: 0 }}>{mod.label}</h3>
              </div>
              <div style={{ padding: '12px', borderRadius: '12px', backgroundColor: mod.bgColor, color: mod.color }}>
                <mod.icon size={26} />
              </div>
            </div>
            <div style={{ marginTop: 'auto' }}>
              <p style={{ fontSize: '14px', color: '#64748b', fontWeight: '500', lineHeight: '1.5', margin: '0 0 16px 0' }}>{mod.desc}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '700', color: mod.color }}>
                <span>Acessar</span> <ArrowRight size={16} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ⚠️ A TRILHA "Como o chamado caminha" SAIU EM 2026-08-28 (Julia:
          "não é necessário"). Eram 3 passos explicando quem faz o quê — abre,
          tria, executa/encerra.
          ⚠️ Ela já tinha sido corrigida uma vez: descrevia um 4º passo ("a loja
          confirma que resolveu") que ficou para trás quando a validação do
          gerente deixou de ser etapa, em 24/08. Ou seja, era um texto que
          envelhecia a cada mudança do fluxo e precisava ser lembrado junto —
          custo que sai agora com ele.
          ⚠️ O fluxo em si NÃO mudou: continua vivo no  (com
          testes) e documentado no CLAUDE.md. O que saiu foi a explicação na
          tela, não a regra. */}

      {/* Agenda dos técnicos — onde cada um vai estar, de acordo com os chamados.
          Fica no FIM da Visão Geral (decisão da Julia, 2026-08-21): vem depois dos
          cards de navegação e da trilha do fluxo. Nasceu no topo, e trocar faz
          sentido — é o bloco mais alto e mais denso da tela, então em primeiro ele
          empurrava para fora da primeira dobra justamente o que dá acesso ao
          módulo (os cards) e o que o explica (a trilha).

          ⚠️⚠️ ESCONDIDO DA LOJA DESDE 2026-08-27 (Julia, "por enquanto") — a outra
             metade da cortina que tirou a aba *Chamados* de `Gerente` e
             `Gerente Plantao`. Escondida a fila e mantido o calendário, o gerente
             veria a agenda dos técnicos sem ter onde acompanhar o próprio chamado:
             meia entrega, que num módulo em desenvolvimento é pior que nenhuma.
          ⚠️ Não montar é diferente de esconder com CSS: o `ChamadosCalendario`
             busca `/api/chamados/agenda` ao montar, e um bloco invisível ainda
             faria a chamada. Quem não vê, não pede.
          ⚠️ Para devolver: a capacidade `chamados.aba.agenda` — junto com a
             `consulta`, que é a outra metade. */}
      {podeVerAgenda && (
        <div style={{ marginTop: '32px' }}>
          <ChamadosCalendario equipe={equipeAlvo} />
        </div>
      )}
    </div>
  );

  useEffect(() => {
    const handleResize = () => { if (window.innerWidth <= 768) setIsCollapsed(true); };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // ⚠️ Só depois das capacidades chegarem: enquanto `caps` está vazio (a
    //    resposta não voltou), TODA aba pareceria proibida e a pessoa seria
    //    jogada para a Visão Geral no meio da navegação.
    if (carregandoCaps) return;
    if (activeTab !== 'dashboard' && !alcanca(activeTab)) {
      setActiveTab('dashboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `alcanca` é derivada de `caps`
  }, [activeTab, caps, carregandoCaps]);

  // ---------------------------------------------------------------------
  // RASTRO DA ABERTURA DO GPS-TI (Manual do TI) — grupo 3 da DEM-00009.
  // ---------------------------------------------------------------------
  // ⚠️ O PageTracker (App.js) NÃO cobre isto: ele dispara em `location.pathname`,
  //    e o Manual é ABA dentro de /chamados — trocar de aba não muda a URL.
  // ⚠️⚠️ UMA VEZ POR VISITA AO MÓDULO, não por render nem por remontagem. O ref
  //    vive enquanto o HUB vive (uma montagem por visita), e o hub NÃO remonta ao
  //    trocar de aba nem de fila (`trocarDeFila` é setState). Quem remonta é o
  //    componente da aba Manual (render condicional do `switch`), e é por isso
  //    que o log mora AQUI e não lá dentro: abrir Manual → trocar de fila → abrir
  //    de novo contaria duas vezes se o disparo fosse na montagem da aba.
  // ⚠️ Rota já existente (`/api/logs/registrar`), sem tabela nova. `acao` é a
  //    mesma 'NAVEGACAO' do PageTracker (cai na mesma trilha); o `detalhe`
  //    identifica o GPS-TI. Best-effort: erro no rastro não atrapalha o guia.
  const gpsTiJaLogado = useRef(false);
  useEffect(() => {
    if (activeTab !== 'manual' || gpsTiJaLogado.current) return;
    gpsTiJaLogado.current = true;
    const token = localStorage.getItem('authToken');
    if (!token) return;
    fetch('/api/logs/registrar', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ acao: 'NAVEGACAO', detalhe: 'Abriu o GPS-TI (Manual do TI)' }),
    }).catch(() => {});
  }, [activeTab]);

  const contentBoxStyle = {
    backgroundColor: '#fff', borderRadius: isMobile ? '12px' : '16px',
    border: `1px solid ${COR_CLARA}`,
    padding: isMobile ? '14px' : '32px',
    minHeight: '75vh', width: '100%', boxSizing: 'border-box',
  };

  const renderContent = () => {
    if (activeTab === 'dashboard') return <OverviewWidgets />;
    switch (activeTab) {
      // ⚠️⚠️ `onIrParaConsulta` SÓ VAI PARA QUEM ENXERGA A CONSULTA. É o botão
      //    "Ir para o controle de chamados" da tela de confirmação. Com a
      //    cortina de 27/08 no ar, mandar o gerente para lá o jogaria numa aba
      //    que não existia — o `useEffect` acima o devolveria à Visão Geral,
      //    engolindo junto o número do chamado que ele precisa anotar.
      //    ⚠️ A cortina caiu em 28/08 e o botão VOLTOU SOZINHO, sem nada a
      //    mudar aqui: é derivado das capacidades, nunca de uma segunda lista
      //    de perfis. Manter a condição é o que faz isso continuar valendo se
      //    algum perfil novo entrar no módulo sem alcançar a fila.
      case 'abrir': return (
        <div style={contentBoxStyle}>
          {/* ⚠️ O Passo 1 (GPS-TI) virou navegação INTERNA em 27/08, quando a
              cópia pública do guia foi desligada. É `alcanca('manual')` e não
              `true`: quem não enxerga a aba não pode receber um botão que leva
              a ela — cairia no `useEffect` de permissão e voltaria para a Visão
              Geral, perdendo o formulário. Hoje todo perfil que abre chamado
              também alcança o manual, mas amarrar as duas coisas evita que uma
              mudança futura nas capacidades crie um botão sem destino. */}
          <ChamadosAbertura
            onIrParaConsulta={podeVerConsulta ? () => irParaAba('consulta') : undefined}
            onIrParaManual={alcanca('manual') ? () => irParaAba('manual') : undefined}
            familiaFiltro={familiaFiltro}
            etiqueta={etiqueta}
            equipe={equipeAlvo}
          />
        </div>
      );
      case 'consulta': return <div style={contentBoxStyle}><ChamadosConsulta equipe={equipeAlvo} /></div>;
      case 'patrimonio': return <div style={contentBoxStyle}><ChamadosPatrimonio /></div>;
      case 'indicadores': return <div style={contentBoxStyle}><ChamadosIndicadores equipe={equipeAlvo} /></div>;
      // ⚠️ `onAbrirChamado` é a ponta React da ponte com o guia: o
      //    `ChamadosManual` escuta o `postMessage` do iframe, traduz a ficha em
      //    família e chama isto. É por aqui — e SÓ por aqui — que o filtro entra.
      case 'manual': return (
        <div style={contentBoxStyle}>
          <ChamadosManual
            onAbrirChamado={alcanca('abrir') ? irParaAbertura : undefined}
          />
        </div>
      );
      case 'ativos': return <div style={contentBoxStyle}><ChamadosAtivos equipe={equipeAlvo} /></div>;
      default: return null;
    }
  };

  const getMarginLeft = () => (window.innerWidth <= 768 ? '0px' : (isCollapsed ? '80px' : '260px'));

  // ---------------------------------------------------------------------
  // A FASE DE TESTE (Julia, 31/08/2026) — TEMPORÁRIA
  // ---------------------------------------------------------------------
  // ⚠️⚠️ O menu lateral já esconde o módulo de quem está fora, então este bloco só
  //    é alcançado por URL DIGITADA (ou link antigo, ou favorito). Sem ele, essa
  //    pessoa veria o hub montar e cada chamada de API voltar 403 — uma tela
  //    quebrada, que parece defeito do portal e vira chamado para o TI.
  //
  // ⚠️ O texto fala em FASE DE TESTE e nomeia as lojas, nunca em permissão: o
  //    gerente da ANV não tem o que pedir ao Admin, e dizer "sem acesso" o faria
  //    procurar quem liberasse.
  //
  // ⚠️ É a MESMA regra do backend (`lojaNoPiloto`), espelhada — ver o aviso no
  //    `escopoUsuario.js`. A trava que vale é a de lá; esta é só para a tela não
  //    mentir.
  if (!lojaNoPilotoChamados(user)) {
    return (
      <div className={`modulo-chamados${equipeAlvo === 'Manutencao' ? ' modulo-chamados--manutencao' : ''}`} style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8f8f8' }}>
        <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} mostrarBotaoMobile={false} />
        <div style={{ flex: 1, marginLeft: getMarginLeft(), transition: 'margin-left 0.3s ease' }}>
          <ModuloHeader
            eyebrow="Módulo"
            titulo="Chamados"
            descricao="Abertura e acompanhamento de chamados"
            cor={COR}
            corEscura={COR_ESCURA}
            icone={LifeBuoy}
          />
          <div style={{ padding: 24, maxWidth: 640 }}>
            <div style={{
              padding: 20, borderRadius: 12,
              backgroundColor: '#fffbeb', border: '1px solid #fde68a',
            }}>
              <p style={{ margin: '0 0 8px 0', fontSize: 15, fontWeight: 800, color: '#78350f' }}>
                Em fase de teste
              </p>
              <p style={{ margin: 0, fontSize: 13.5, color: '#92400e', lineHeight: 1.6 }}>
                O módulo de Chamados está sendo testado e, por enquanto, atende apenas{' '}
                <strong>{LOJAS_PILOTO_CHAMADOS.join(', ')}</strong>. A sua loja entra em breve —
                até lá, siga abrindo chamado pelo caminho de sempre.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }


  // ⚠️⚠️ ESPERA AS CAPACIDADES ANTES DE DESENHAR, e isto não é zelo: renderizar
  //    com a lista vazia mostraria o hub SEM NENHUMA ABA por um instante, e a
  //    portaria mandaria a pessoa para o lugar errado — o destino de entrada é
  //    derivado de quem ela alcança. Meio segundo de "carregando" é melhor do
  //    que uma tela que pisca vazia e decide errado.
  if (carregandoCaps) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
        Carregando suas permissões…
      </div>
    );
  }

  // ⚠️ FALHA FECHADA: sem a resposta, não se adivinha o que a pessoa alcança —
  //    desenhar tudo produziria botões que voltam 403 e pareceria defeito do
  //    portal. A mensagem diz o que fazer, em vez de deixar a tela muda.
  if (erroCaps) {
    return (
      <div style={{ padding: '60px', textAlign: 'center', color: '#9f1239', fontSize: '14px' }}>
        Não foi possível carregar suas permissões. Recarregue a página.
        <div style={{ fontSize: '12.5px', color: '#94a3b8', marginTop: '8px' }}>{erroCaps}</div>
      </div>
    );
  }

  return (
    // ⚠️ `modulo-chamados` NÃO é decoração: é o gancho das regras de celular no
    //    `index.css` E, desde 17/09/2026, o gancho da COR DA FILA — o
    //    modificador `--manutencao` redefine as variáveis de cor do módulo
    //    (índigo no lugar do roxo) para TODAS as telas de dentro, sem que
    //    nenhuma delas precise receber a cor por prop.
    //    `index.css` (a principal delas impede o iOS de dar zoom sozinho ao tocar
    //    num campo). Tirar a classe reintroduz o problema em silêncio.
    <div className={`modulo-chamados${equipeAlvo === 'Manutencao' ? ' modulo-chamados--manutencao' : ''}`} style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f8f8f8' }}>
      {/* ⚠️ A PORTARIA VEM ANTES DE TUDO e é bloqueante (2026-08-28). Ela cobre a
          tela inteira até a pessoa escolher — inclusive a Sidebar, de propósito:
          escolher o departamento é a primeira coisa que se faz aqui.
          ⚠️ O módulo continua MONTADO atrás dela (não é um `return` antecipado):
          escolher T.I precisa apenas trocar a aba, sem remontar nada e sem
          recarregar as listas que já vieram. */}
      {/* ⚠️⚠️ O DESTINO DO T.I MUDOU EM 2026-08-31 (Julia): era o MANUAL, virou
          ABRIR CHAMADO. A portaria nasceu três dias antes justamente para pôr o
          guia na frente do formulário — e a inversão foi revertida porque quem
          chega aqui pela portaria já decidiu abrir chamado; mandá-lo ao guia
          fazia dele um pedágio, e o caminho ficava com um clique a mais para o
          ato que a pessoa veio fazer.
          ⚠️ O GUIA NÃO SUMIU DO CAMINHO — a `ChamadosAbertura` abre com o bloco
          *Passo 1 — Abrir o GPS-TI*, no topo, antes do formulário. A ordem
          "manual primeiro" continua ENSINADA pela tela; ela só deixou de ser
          imposta pela navegação.
          ⚠️ Cascata em vez de `irParaAba('abrir')` cru: hoje quem enxerga o
          manual também enxerga a abertura, mas amarrar as duas listas evita que
          uma mudança futura nas capacidades mande alguém para uma aba que o
          `useEffect` de falha fechada devolveria à Visão Geral no quadro
          seguinte — um piscar sem explicação. */}
      {!passouPortaria && (
        <ChamadosPortaria
          filas={filasVisiveis}
          onEscolher={(equipe) => {
            // ⚠️ A EQUIPE É GRAVADA ANTES de trocar a aba: o formulário lê a prop
            //    na montagem para escolher o catálogo de equipamento, e a ordem
            //    inversa o montaria pedindo o parque de TI numa fila que não é a
            //    dele.
            setEquipeAlvo(equipe);
            setPassouPortaria(true);
            // ⚠️ O GUIA É DO TI. Mandar quem escolheu Manutenção para o manual
            //    (a cascata de quando a abertura não é alcançável) o levaria a um
            //    documento que não fala da fila dele; nesse caso a Visão Geral é
            //    a resposta honesta.
            const alternativa = equipe === 'TI' && alcanca('manual') ? 'manual' : 'dashboard';
            irParaAba(alcanca('abrir') ? 'abrir' : alternativa);
          }}
        />
      )}
      <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} mostrarBotaoMobile={false} />
      <div style={{ flex: 1, marginLeft: getMarginLeft(), transition: 'margin-left 0.3s ease' }}>

        <ModuloHeader
          eyebrow="Módulo"
          // ⚠️⚠️ A FILA NO TÍTULO **SÓ NO COMPUTADOR** (17/09/2026). O
          //    `ModuloHeader` corta o título com reticências (`whiteSpace:
          //    nowrap`), e num aparelho de 360px sobram ~190px depois do
          //    hambúrguer e dos ícones de ação: `Chamados · Manutenção` vira
          //    `Chamados · Manut…` — ou seja, no celular o pedaço que IDENTIFICA a
          //    área é justamente o primeiro a sumir.
          //    No aparelho quem responde "onde estou" é a FAIXA logo abaixo, que
          //    ocupa a largura inteira e tem ícone e cor próprios.
          titulo={isMobile ? 'Chamados' : `Chamados · ${rotuloDaFila(equipeAlvo)}`}
          cor={COR}
          onAbrirManual={() => setIsHelpOpen(true)}
          onAbrirMenu={() => setIsCollapsed(false)}
          isMobile={isMobile}
        />

        {/* ⚠️ No celular o respiro cai de 40px para 12px. Somado aos 32px da caixa
            de dentro, o desenho de computador consumia 144px dos 360px do
            aparelho — quase metade da tela virava margem. */}
        <div style={{ padding: isMobile ? '12px' : '40px', maxWidth: '1600px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>

          {/* ⚠⚠ A FAIXA DE FILA VEM ANTES DAS ABAS (17/09/2026), e a ordem é a
              decisão: as abas mudam de conteúdo conforme a fila, então quem lê de
              cima para baixo precisa saber em qual fila está ANTES de escolher a
              aba. Depois delas, a faixa explicaria uma escolha já feita.
              ⚠️ Ela aparece nas duas filas, inclusive para quem só tem T.I — ver o
              porquê no próprio componente (ausência não se lê). */}
          <ChamadosFaixaFila
            fila={equipeAlvo}
            filas={filasVisiveis}
            onTrocar={trocarDeFila}
            isMobile={isMobile}
            compacto={ehTabletOuMenor}
          />

          <div style={{
            display: 'flex', flexWrap: 'wrap',
            gap: isMobile ? '6px' : '10px', marginBottom: isMobile ? '16px' : '32px',
            backgroundColor: '#fff', padding: isMobile ? '6px' : '8px', borderRadius: '12px',
            border: `1px solid ${COR_CLARA}`, width: '100%', boxSizing: 'border-box',
          }}>
            <button
              onClick={() => irParaAba('dashboard')}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: isMobile ? '10px 12px' : '10px 20px', borderRadius: '8px', border: 'none',
                fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s',
                // ⚠️ Sem `fontSize` no computador DE PROPÓSITO: o padrão do <button>
                //    (~13px) é o que todos os outros hubs do portal usam. Um
                //    `fontSize: 'inherit'` aqui puxava os 16px do corpo da página e
                //    deixava as abas do Chamados visivelmente maiores que as dos
                //    demais módulos. No celular, `minHeight` garante o alvo de toque.
                ...(isMobile ? { fontSize: '13px', minHeight: 40 } : {}),
                background: activeTab === 'dashboard' ? `linear-gradient(135deg, ${COR} 0%, ${COR_ESCURA} 100%)` : 'transparent',
                color: activeTab === 'dashboard' ? '#fff' : '#64748b',
                boxShadow: activeTab === 'dashboard' ? '0 4px 6px var(--ch-sombra)' : 'none',
                whiteSpace: 'nowrap',
              }}
            >
              <LayoutDashboard size={18} /> Visão Geral
            </button>

            {/* ⚠️ O separador some no celular: com as abas quebrando em várias
                linhas, ele deixa de separar duas metades e vira um risco solto no
                meio da segunda linha. */}
            {!isMobile && <div style={{ minWidth: '1px', backgroundColor: COR_CLARA, margin: '0 5px' }} />}

            {allowedModules.map((mod) => (
              <button
                key={mod.id}
                onClick={() => irParaAba(mod.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: isMobile ? '10px 12px' : '10px 20px', borderRadius: '8px', fontWeight: '600',
                  cursor: 'pointer', transition: 'all 0.2s',
                  // Mesma razão do botão acima: no computador vale o tamanho padrão
                  // do <button>, para as abas casarem com as dos outros módulos.
                  ...(isMobile ? { fontSize: '13px', minHeight: 40 } : {}),
                  border: activeTab === mod.id ? `2px solid ${mod.color}` : '2px solid transparent',
                  backgroundColor: activeTab === mod.id ? mod.bgColor : 'transparent',
                  color: activeTab === mod.id ? mod.color : '#64748b',
                  whiteSpace: 'nowrap',
                }}
              >
                <mod.icon size={18} /> {mod.label}
              </button>
            ))}
          </div>

          {renderContent()}
        </div>
      </div>

      {/* Manual da Tela — verbetes do módulo (HelpCenter › manualChamados).
          ⚠️ `modulo` é obrigatório: sem ele o HelpCenter cai no "junta tudo" e
          mostra os verbetes de TODOS os módulos do portal. */}
      <HelpCenter
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        modulo="chamados"
        secaoInicial={activeTab === 'dashboard' ? 'visao-geral-chamados' : `chamados-${activeTab}`}
      />
    </div>
  );
};

export default ChamadosPainel;
